import { Link } from "react-router-dom";
import { UserPlus, Heart, ArrowRight, Search, Star, Sparkles, TrendingUp, DollarSign } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { RoleGate } from "@/components/RoleGate";

const CREATOR_STEPS = [
  { n: "1", t: "Join", d: "Create your creator account in minutes." },
  { n: "2", t: "Build", d: "Set up your profile, content, and offers." },
  { n: "3", t: "Grow", d: "We help you get discovered and gain real fans." },
  { n: "4", t: "Earn", d: "Get paid, track your performance, and scale." },
];
const FAN_STEPS = [
  { n: "1", t: "Discover", d: "Find creators based on your interests." },
  { n: "2", t: "Connect", d: "Follow your favorites and stay updated." },
  { n: "3", t: "Support", d: "Subscribe, tip, or buy content." },
  { n: "4", t: "Enjoy", d: "Get exclusive access and interact." },
];

export default function Home() {
  return (
    <div>
      <RoleGate />
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center pt-28 pb-16 overflow-hidden grain" data-testid="hero">
        <div className="absolute -top-40 -left-20 w-[520px] h-[520px] rounded-full bg-pink-500/20 blur-3xl" />
        <div className="absolute -bottom-40 -right-20 w-[560px] h-[560px] rounded-full bg-violet-600/20 blur-3xl" />

        <div className="relative z-10 max-w-5xl mx-auto px-6 w-full text-center">
          <Reveal>
            <div className="inline-flex items-center gap-2 border border-white/15 bg-white/5 rounded-full px-4 py-1.5 mb-6">
              <Sparkles size={14} className="text-brand" />
              <span className="text-xs font-medium text-white/80">The Connection Platform</span>
            </div>
            <h1 className="font-display text-5xl sm:text-6xl md:text-7xl text-white leading-[1.05]" data-testid="hero-headline">
              The Connection Platform<br />for <span className="text-brand">Creators</span> and <span className="text-brand">Fans</span>
            </h1>
            <p className="mt-6 text-lg text-white/70 max-w-2xl mx-auto">
              Discover. Grow. Earn. Support. Fan Focus is the all-in-one platform where creators build their business and fans find the people they love.
            </p>
            <p className="mt-8 text-sm uppercase tracking-widest text-white/50">Choose your experience</p>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="mt-6 grid sm:grid-cols-2 gap-5 max-w-3xl mx-auto">
              <div className="card p-6 text-left hover:-translate-y-1 hover:border-pink-500/50 transition-all duration-300" data-testid="choice-creator">
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-11 w-11 rounded-full bg-pink-500/15 grid place-items-center"><UserPlus className="text-brand" size={20} /></div>
                  <h3 className="font-display text-2xl text-brand">I'm a Creator</h3>
                </div>
                <p className="text-white/65 text-sm mb-5">Build your brand. Grow your audience. Get paid. We'll help you scale.</p>
                <Link to="/signup?type=creator" data-testid="cta-creator"
                  className="w-full inline-flex items-center justify-center gap-2 bg-brand text-white py-3 rounded-lg font-semibold hover:opacity-90 transition-opacity">
                  Join as a Creator <ArrowRight size={16} />
                </Link>
              </div>
              <div className="card p-6 text-left hover:-translate-y-1 hover:border-violet-500/50 transition-all duration-300" data-testid="choice-fan">
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-11 w-11 rounded-full bg-violet-500/15 grid place-items-center"><Heart className="text-violet-400" size={20} /></div>
                  <h3 className="font-display text-2xl text-violet-300">I'm a Fan</h3>
                </div>
                <p className="text-white/65 text-sm mb-5">Discover creators you love. Connect, support, and get closer. Free forever.</p>
                <Link to="/signup?type=fan" data-testid="cta-fan"
                  className="w-full inline-flex items-center justify-center gap-2 bg-violet-500 text-white py-3 rounded-lg font-semibold hover:opacity-90 transition-opacity">
                  Join Free as a Fan <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-6 py-24" data-testid="how-it-works">
        <Reveal className="text-center mb-14">
          <h2 className="font-display text-4xl md:text-5xl text-white">How It Works</h2>
          <div className="gold-line mx-auto mt-4" />
        </Reveal>
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Creators */}
          <div className="card p-8">
            <p className="overline text-brand mb-6">For Creators</p>
            <div className="space-y-5">
              {CREATOR_STEPS.map((s, i) => (
                <Reveal key={i} delay={i * 0.05}>
                  <div className="flex gap-4">
                    <div className="h-10 w-10 rounded-full bg-pink-500/15 text-brand grid place-items-center font-semibold shrink-0">{s.n}</div>
                    <div>
                      <div className="text-white font-semibold">{s.t}</div>
                      <div className="text-white/60 text-sm">{s.d}</div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
          {/* Fans */}
          <div className="card p-8">
            <p className="overline text-violet-300 mb-6">For Fans</p>
            <div className="space-y-5">
              {FAN_STEPS.map((s, i) => (
                <Reveal key={i} delay={i * 0.05}>
                  <div className="flex gap-4">
                    <div className="h-10 w-10 rounded-full bg-violet-500/15 text-violet-300 grid place-items-center font-semibold shrink-0">{s.n}</div>
                    <div>
                      <div className="text-white font-semibold">{s.t}</div>
                      <div className="text-white/60 text-sm">{s.d}</div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Value bar */}
      <section className="border-y border-white/10 bg-brand-soft py-14" data-testid="value-bar">
        <div className="max-w-7xl mx-auto px-6 grid sm:grid-cols-3 gap-8 text-center">
          {[[Star,"Creator-First","Every feature is built for your growth."],
            [TrendingUp,"Real Growth","We help creators build lasting careers."],
            [DollarSign,"Fair Earnings","Keep 70% of every dollar you earn."]].map(([I,t,d],i)=>(
            <Reveal key={i} delay={i*0.08}>
              <I className="text-brand mx-auto mb-3" size={26} strokeWidth={1.6} />
              <div className="font-semibold text-white">{t}</div>
              <p className="text-white/60 text-sm mt-1">{d}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-4xl mx-auto px-6 py-24 text-center">
        <Reveal>
          <h2 className="font-display text-4xl md:text-5xl text-white leading-tight">Ready to <span className="text-brand">Get Started?</span></h2>
          <p className="mt-4 text-white/60 max-w-lg mx-auto">Whether you create or you cheer them on — Fan Focus is where it happens.</p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link to="/signup?type=creator" data-testid="cta-creator-bottom" className="inline-flex items-center gap-2 bg-brand text-white px-8 py-3.5 rounded-lg font-semibold">Join as a Creator <ArrowRight size={16} /></Link>
            <Link to="/signup?type=fan" data-testid="cta-fan-bottom" className="inline-flex items-center gap-2 border border-white/25 text-white px-8 py-3.5 rounded-lg font-semibold hover:border-brand hover:text-brand transition-colors">Join Free as a Fan <Search size={15} /></Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
